import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Close } from '@videoblocks/react-icons';

import {
  selectAuthFeatureFlags,
  selectAuthPermissions,
  selectHasAnySubscription,
  selectUseSnapshotsUI,
} from '../../auth/AuthSelectors';
import { searchOrigins } from '../Navigation/constants';
import { isSearchAppLoaded } from '../Search/actions/SearchActions';
import {
  ConversationalSearchProvider,
  NavSearchContextProvider,
} from '../Search/components/navSearch/NavSearchContext';
import CrossAssetNavSearchBox from '../Search/components/navSearch/NavSearchElements/CrossAssetNavSearchBox';
import {
  cleanSearchKeywords,
  SearchBarVariant,
} from '../Search/components/navSearch/NavSearchElements/searchUtils';
import ProminentSearchDropdown from '../Search/components/navSearch/ProminentSearchElements/ProminentSearchDropdown';
import { handleSubmit as handleSearchSubmit } from '../Search/containers/NavSearchContainer';
import { useAutoDisableConversationalSearch } from '../Search/hooks/useAutoDisableConversationalSearch';
import { useSearchWithAI } from '../Search/hooks/useSearchWithAI';
import {
  selectIsEnterpriseMember,
  selectSearchFilterOptions,
} from '../Search/selectors/searchSelectors';
import { fetchTypeahead } from '../Search/utils/searchUtils';
import { SearchFilterContentTypes } from '../Shared/enums';
import TopNavSearchToggle from './TopNavSearchToggle';

interface TopNavSearchBarProps {
  onCollapse: () => void;
  showXButton: boolean;
}

/**
 * Owns the conversational search on/off state above the inner
 * NavSearchContextProvider so the toggle (inside the form) and the submit
 * handler (in TopNavSearchBarContent) share it — the same pattern SearchHero
 * uses around the search page's prominent search bar.
 */
export default function TopNavSearchBar(
  props: TopNavSearchBarProps
): JSX.Element {
  return (
    <ConversationalSearchProvider>
      <TopNavSearchBarContent {...props} />
    </ConversationalSearchProvider>
  );
}

function TopNavSearchBarContent({
  onCollapse,
  showXButton,
}: TopNavSearchBarProps): JSX.Element {
  const dispatch = useDispatch();

  const selectedSearchFilterOptions = useSelector(selectSearchFilterOptions);
  const userHasAnySubscription = useSelector(selectHasAnySubscription);
  const isEnterpriseMember = useSelector(selectIsEnterpriseMember);
  const { pproPluginEnabled } = useSelector(selectAuthPermissions);
  const useSnapshotsUI = useSelector(selectUseSnapshotsUI);
  const { showConversationalSearch } = useSelector(selectAuthFeatureFlags);

  const { contentType: reduxContentType = '' } = selectedSearchFilterOptions;

  const [contentType, setContentType] = useState<SearchFilterContentTypes>(
    reduxContentType as SearchFilterContentTypes
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState({
    keywords: [],
    relatedCollections: [],
    latestCollections: [],
    trendingCollections: [],
    relatedContentTypesAndKeywords: [],
  });

  const {
    showAiCta,
    getPlaceholder,
    handleConversationalRedirect,
    isSearchWithAIEnabledForContentType,
    conversationalSearchEnabled,
  } = useSearchWithAI({
    showConversationalSearchCta: Boolean(showConversationalSearch),
  });

  useAutoDisableConversationalSearch(
    contentType,
    isSearchWithAIEnabledForContentType
  );

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setContentType(reduxContentType as SearchFilterContentTypes);
  }, [reduxContentType]);

  const clearSuggestions = useCallback(
    () =>
      setSuggestions({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
      }),
    []
  );

  const handleDropdownChange = (value: string) => {
    setContentType(value as SearchFilterContentTypes);
  };

  const handleSubmit = (
    contentTypeSearched = contentType,
    value = searchTerm,
    searchOriginOverride = ''
  ) => {
    const cleanedSearchKeywords = cleanSearchKeywords(value);
    const searchOrigin = searchOriginOverride || searchOrigins.TOP_NAV;
    if (
      handleConversationalRedirect(
        cleanedSearchKeywords,
        searchOrigin,
        contentTypeSearched
      )
    ) {
      return;
    }
    handleSearchSubmit(
      contentTypeSearched,
      cleanedSearchKeywords,
      searchOrigin,
      dispatch,
      selectedSearchFilterOptions
    );
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSubmit(contentType, searchTerm);
  };

  const handleOnClose = () => {
    if (searchTerm.trim()) {
      setSearchTerm('');
      clearSuggestions();
    } else {
      onCollapse();
    }
  };

  return (
    <NavSearchContextProvider
      contentType={contentType}
      onSubmit={handleSubmit}
      userHasAnySubscription={userHasAnySubscription}
      fetchTypeahead={fetchTypeahead}
      isEnterpriseMember={isEnterpriseMember}
      pproPluginEnabled={pproPluginEnabled}
      isSearchAppLoaded={isSearchAppLoaded}
      useSnapshots={useSnapshotsUI}
      searchBarVariant={SearchBarVariant.TopNav}
    >
      {/* The AI restyle (taller intrinsic-height pill, hairline border, text
          dropdown) must not leak to users without the conversational search
          flag — they keep the original fixed-height bar untouched. */}
      <form
        ref={formRef}
        className={
          showAiCta
            ? 'w-full max-w-1034px mx-auto bg-white border rounded-full flex items-center gap-4 relative accessible-focus-input'
            : 'w-full max-w-1034px mx-auto bg-white border-2 border-gray-300 rounded-full flex items-center justify-between px-2 h-12 relative good accessible-focus-input'
        }
        style={
          showAiCta
            ? {
                borderColor: '#d4d6d9',
                paddingTop: 9,
                paddingBottom: 9,
                paddingLeft: 9,
                paddingRight: 17,
              }
            : undefined
        }
        onSubmit={handleFormSubmit}
        data-cy="topnav-search-bar"
      >
        {showAiCta && (
          <TopNavSearchToggle
            disabledForContentType={
              !isSearchWithAIEnabledForContentType(contentType)
            }
          />
        )}
        <ProminentSearchDropdown
          onChange={handleDropdownChange}
          value={contentType}
          useNewSearchUI
          useSnapshots={useSnapshotsUI}
          clearSuggestions={clearSuggestions}
          size="compact"
          variant={showAiCta ? 'text' : 'dark'}
        />
        <div className="flex-1 min-w-0">
          <CrossAssetNavSearchBox
            inputClassName="focus-visible:ring-0"
            contentType={contentType}
            setSearchTerm={setSearchTerm}
            onSubmit={handleSubmit}
            searchTerm={searchTerm}
            suggestions={suggestions}
            setSuggestions={setSuggestions}
            clearSuggestions={clearSuggestions}
            placeholder={
              // Re-check content-type support synchronously: the enabled flag
              // lags a content-type change by one paint (auto-disable runs in
              // an effect), which would flash the AI placeholder otherwise.
              conversationalSearchEnabled &&
              isSearchWithAIEnabledForContentType(contentType)
                ? getPlaceholder(undefined, contentType)
                : undefined
            }
          />
        </div>
        {showXButton && (
          <button
            type="button"
            onClick={handleOnClose}
            aria-label="Clear or close search"
            className={`${
              showAiCta ? '' : 'mr-2 '
            }flex h-4 w-4 flex-shrink-0 items-center justify-center text-gray-500 hover:text-gray-700`}
          >
            <Close className="w-4 h-4" />
          </button>
        )}
      </form>
    </NavSearchContextProvider>
  );
}
